import React, { useState } from 'react';
import {
  Box, Container, Grid, Typography, Avatar, IconButton,
} from '@mui/material';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import StarIcon from '@mui/icons-material/Star';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useLang } from '../context/LanguageContext';

const SectionLabel = ({ children }) => (
  <Box display="flex" alignItems="center" gap={2} mb={2}>
    <Box sx={{ width: 40, height: 3, background: '#C8102E', flexShrink: 0 }} />
    <Typography sx={{
      fontFamily: '"Oswald", sans-serif',
      color: '#C8102E',
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      fontSize: '0.78rem',
      fontWeight: 500,
    }}>
      {children}
    </Typography>
  </Box>
);

export default function Testimonials() {
  const { t } = useLang();
  const tr = t.testimonials;
  const [current, setCurrent] = useState(0);

  const visible = [tr.items[current], tr.items[(current + 1) % tr.items.length]];

  return (
    <Box id="testimonials" sx={{ py: { xs: 7, md: 11 }, background: '#FAFAFA' }}>
      <Container maxWidth="xl">
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">

          {/* Left panel */}
          <Grid item xs={12} md={4}>
            <SectionLabel>{tr.eyebrow}</SectionLabel>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.9rem', md: '2.5rem' }, mb: 3, lineHeight: 1.15 }}>
              {tr.heading}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 3, fontWeight: 300, fontSize: '0.95rem' }}>
              {tr.subtext}
            </Typography>

            <Box mb={3}>
              <Box display="flex" gap={0.4} mb={0.5}>
                {[1,2,3,4,5].map((s) => (
                  <StarIcon key={s} sx={{ color: '#C8102E', fontSize: 18 }} />
                ))}
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{tr.ratingText}</Typography>
            </Box>

            <Box display="flex" gap={1}>
              <IconButton
                onClick={() => setCurrent((prev) => (prev - 1 + tr.items.length) % tr.items.length)}
                sx={{ border: '2px solid #222', borderRadius: 0, '&:hover': { background: '#C8102E', borderColor: '#C8102E', color: '#fff' }, transition: 'all 0.2s ease' }}
              >
                <ArrowBackIcon />
              </IconButton>
              <IconButton
                onClick={() => setCurrent((prev) => (prev + 1) % tr.items.length)}
                sx={{ border: '2px solid #222', borderRadius: 0, '&:hover': { background: '#C8102E', borderColor: '#C8102E', color: '#fff' }, transition: 'all 0.2s ease' }}
              >
                <ArrowForwardIcon />
              </IconButton>
            </Box>
          </Grid>

          {/* Right: cards */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              {visible.map((item, i) => (
                <Grid item xs={12} sm={6} key={item.name + i}>
                  <Box sx={{
                    background: i === 0 ? '#111111' : '#FFFFFF',
                    p: { xs: 3, md: 4 },
                    height: '100%',
                    border: i === 1 ? '1px solid #E8E8E8' : 'none',
                    position: 'relative',
                  }}>
                    <FormatQuoteIcon sx={{
                      color: i === 0 ? 'rgba(200,16,46,0.28)' : 'rgba(200,16,46,0.12)',
                      fontSize: 50,
                      position: 'absolute',
                      top: 14,
                      right: 14,
                    }} />

                    <Box display="flex" gap={0.3} mb={2.5}>
                      {Array.from({ length: item.rating }).map((_, s) => (
                        <StarIcon key={s} sx={{ color: '#C8102E', fontSize: 15 }} />
                      ))}
                    </Box>

                    <Typography variant="body1" sx={{
                      color: i === 0 ? '#CCCCCC' : 'text.secondary',
                      lineHeight: 1.85,
                      fontStyle: 'italic',
                      mb: 3,
                      fontWeight: 300,
                      fontSize: { xs: '0.88rem', md: '0.92rem' },
                    }}>
                      "{item.quote}"
                    </Typography>

                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                      <Avatar sx={{
                        background: '#C8102E',
                        width: 42,
                        height: 42,
                        fontFamily: '"Oswald", sans-serif',
                        fontWeight: 600,
                        fontSize: '0.82rem',
                        letterSpacing: '0.05em',
                        flexShrink: 0,
                      }}>
                        {item.initials}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{
                          fontFamily: '"Oswald", sans-serif',
                          fontWeight: 500,
                          fontSize: '0.88rem',
                          color: i === 0 ? '#fff' : '#111',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                        }}>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: i === 0 ? '#666' : '#999', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.62rem' }}>
                          {item.company}
                        </Typography>
                      </Box>
                      <Box sx={{
                        ml: 'auto',
                        px: 1.2,
                        py: 0.3,
                        background: i === 0 ? 'rgba(200,16,46,0.2)' : '#F0F0F0',
                        flexShrink: 0,
                      }}>
                        <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: i === 0 ? '#C8102E' : '#888' }}>
                          {item.industry}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

      </Container>
    </Box>
  );
}
